import { useState, type FormEvent } from 'react';
import { useSession } from '../../lib/auth/session.js';
import { useExtensions, useCreateExtension, useDeleteExtension } from '../../lib/api/hooks.js';
import { StatusBadge, TableSkeleton, Empty, ErrorBanner } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { ApiClientError } from '../../lib/api/client.js';

/**
 * Reused in two places: standalone, and as a tab inside an account.
 * `tenantId` is passed explicitly when embedded so the tab always shows the
 * account in the URL rather than whatever tenant happens to be selected
 * elsewhere -- otherwise a support link could open the wrong customer's data.
 */
export function ExtensionsPage({ tenantId: propTenantId, embedded }: {
  tenantId?: string;
  embedded?: boolean;
} = {}) {
  const activeTenantId = useSession((s) => s.activeTenantId);
  const tenantId = propTenantId ?? activeTenantId;
  const can = useSession((s) => s.can);
  const [q, setQ] = useState('');
  const [adding, setAdding] = useState(false);

  const { data, isLoading, error } = useExtensions(tenantId, { q: q || undefined, limit: 100 });
  const del = useDeleteExtension(tenantId ?? '');

  if (!tenantId) {
    return <Empty icon="accounts" title="No tenant selected" hint="Choose a tenant to manage its extensions." />;
  }

  return (
    <>
      <div className="arc-page-head">
        <div>
          {!embedded && <h1 className="arc-h1">Extensions</h1>}
          {!embedded && (
            <p className="arc-muted" style={{ marginTop: 3 }}>
              Internal numbers and their handsets
            </p>
          )}
        </div>
        {can('extension:write') && (
          <button type="button" className="arc-btn arc-btn-primary" onClick={() => setAdding((v) => !v)}>
            {adding ? 'Cancel' : '+ New extension'}
          </button>
        )}
      </div>

      {error && <ErrorBanner error={error} />}
      {adding && <NewExtensionForm tenantId={tenantId} onDone={() => setAdding(false)} />}

      <div className="arc-card">
        <div className="arc-toolbar">
          <div className="arc-search">
            <input
              className="arc-input"
              placeholder="Search by number or name…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              aria-label="Search extensions"
            />
          </div>
          <div className="arc-spacer" />
          <span className="arc-subtle arc-num" style={{ fontSize: 12.5 }}>
            {data ? `${data.data.length} shown` : ''}
          </span>
        </div>

        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr>
                <th>Number</th>
                <th>Name</th>
                <th>Caller ID</th>
                <th>Status</th>
                <th className="arc-td-right">Max calls</th>
                <th />
              </tr>
            </thead>
            {isLoading ? (
              <TableSkeleton cols={6} />
            ) : (
              <tbody>
                {data?.data.map((e) => (
                  <tr key={e.id}>
                    <td className="arc-mono" style={{ fontWeight: 600 }}>{e.number}</td>
                    <td>{e.display_name}</td>
                    <td className="arc-mono arc-subtle">{e.outbound_caller_id_number ?? '—'}</td>
                    <td>
                      <div className="arc-row" style={{ gap: 6 }}>
                        <StatusBadge status={e.status} />
                        {e.do_not_disturb && <span className="arc-badge arc-badge-warn">DND</span>}
                      </div>
                    </td>
                    <td className="arc-td-right arc-num">{e.max_concurrent_calls}</td>
                    <td className="arc-td-right">
                      {can('extension:delete') && (
                        <button
                          type="button"
                          className="arc-btn arc-btn-ghost arc-btn-sm"
                          onClick={() => {
                            if (confirm(`Delete extension ${e.number} (${e.display_name})?`)) {
                              del.mutate(e.id);
                            }
                          }}
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>

        {!isLoading && data?.data.length === 0 && (
          <Empty icon="extension" title="No extensions" hint="Add one to start making calls." />
        )}
      </div>
    </>
  );
}

function NewExtensionForm({ tenantId, onDone }: { tenantId: string; onDone: () => void }) {
  const create = useCreateExtension(tenantId);
  const [number, setNumber] = useState('');
  const [displayName, setDisplayName] = useState('');

  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate(
      { number, display_name: displayName },
      { onSuccess: () => { setNumber(''); setDisplayName(''); onDone(); } },
    );
  };

  const fieldErrors = create.error instanceof ApiClientError ? create.error.fieldErrors : {};
  const generalError =
    create.error instanceof ApiClientError && Object.keys(fieldErrors).length === 0
      ? create.error.message : null;

  return (
    <form className="arc-card arc-card-body" style={{ marginBottom: 16 }} onSubmit={submit}>
      <h2 className="arc-h2" style={{ marginBottom: 14 }}>New extension</h2>

      {generalError && (
        <div className="arc-banner arc-banner-danger" style={{ marginBottom: 14 }} role="alert">
          <Icon name="warning" size={14} /><span>{generalError}</span>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr auto', gap: 12, alignItems: 'start' }}>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="ext-number">Number</label>
          <input
            id="ext-number" className="arc-input" inputMode="numeric"
            placeholder="1003" required value={number}
            onChange={(e) => setNumber(e.target.value)}
            aria-invalid={!!fieldErrors['number']}
          />
          {fieldErrors['number']
            ? <div className="arc-error">{fieldErrors['number']}</div>
            : <div className="arc-hint">2–6 digits. 91xx is reserved.</div>}
        </div>

        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="ext-name">Display name</label>
          <input
            id="ext-name" className="arc-input"
            placeholder="Reception" required value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            aria-invalid={!!fieldErrors['display_name']}
          />
          {fieldErrors['display_name'] && <div className="arc-error">{fieldErrors['display_name']}</div>}
        </div>

        <div style={{ paddingTop: 25 }}>
          <button type="submit" className="arc-btn arc-btn-primary" disabled={create.isPending}>
            {create.isPending ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>
    </form>
  );
}
